import { ArrowBackIos, Edit } from '@mui/icons-material'
import { Box, Button, Typography } from '@mui/material'
import { SyntheticEvent, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import {
  CaseStatus,
  ExternalUser,
  ExternalUserRoleRole,
  useFetchAllCasesLazyQuery,
  useGetClientByClientNumberQuery
} from '../../generated'
import { AllCases } from '../components/cases/allCases'
import { CaseActionType } from '../components/cases/caseReducer'
import { CommonLoader } from '../components/common/CommonLoader'
import { a11yProps, TabPanel } from '../components/common/TabPanel'
import ClientGeneralCard from '../components/externalUsers/client/ClientGeneralCard'
import { GRAPHQL_QUERY_POLICY, QUERY_PARAMS } from '../constants'
import { AppContext, CaseContext } from '../context'
import { ActionType } from '../context/AppContextReducer'
import palette from '../theme/palette'
import { TabItem, TabsContainer } from '../theme/styleComponents'
import ClientInvoicesCard from '../components/externalUsers/client/ClientInvoicesCard'
import { EditClient } from '../components/externalUsers/EditClient'

const ClientDetailContainer = () => {
  const navigate = useNavigate()
  const { clientNo } = useParams()

  const [searchParams, setSearchParams] = useSearchParams()
  const [client, setClient] = useState<ExternalUser | null>(null)

  const searchValue = searchParams.get('searchValue')
  const activeTab = parseInt(searchParams.get('activeTab') || '0')
  const [debouncedSearch] = useDebounce(searchValue, 500)

  const { dispatch: reducerDispatch, currentPage } = useContext(AppContext)
  const { pageValue, lawTypeId, dispatch: caseDispatch, caseFilter, sortCasesByUser, orderBy } = useContext(CaseContext)

  const handleBack = () => navigate(-1)
  const handleChange = (event: SyntheticEvent, newValue: number) => {
    caseDispatch({ type: CaseActionType.SET_PAGE_VALUE, pageValue: newValue })
  }

  const handleOpenEditModal = () => reducerDispatch({ type: ActionType.SET_IS_EDIT, isEdit: true })

  const [fetchAllCases, { loading }] = useFetchAllCasesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
      caseDispatch({ type: CaseActionType.SET_ALL_CASES, allCases: [] })
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllCases } = data

        if (fetchAllCases) {
          const { cases, pagination } = fetchAllCases

          if (cases) {
            caseDispatch({ type: CaseActionType.SET_ALL_CASES, allCases: cases || [] })
            if (!client) {
              const clientInfo = cases
                .flatMap(matter => matter?.caseToExternalUserToExternalUserRole || [])
                .find(
                  item =>
                    item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles &&
                    item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles[0]?.externalUserRole?.role ===
                      ExternalUserRoleRole.Client
                )?.externalUserToExternalUserRole.externalUser

              if (clientInfo) setClient(clientInfo as ExternalUser)
            }
          }

          if (pagination) {
            const { totalPages, totalCount } = pagination
            totalPages && reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
            totalCount && reducerDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
            reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
          }
        }
      }
    }
  })

  const { refetch } = useGetClientByClientNumberQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: {
      clientNumber: clientNo || ''
    },
    onCompleted: data => {
      const { getClientByClientNumber } = data || {}
      const { externalUser } = getClientByClientNumber || {}
      if (externalUser) setClient(externalUser)
    }
  })

  useEffect(() => {
    if (pageValue === 1 || activeTab === 1) {
      fetchAllCases({
        variables: {
          casesInput: {
            paginationOptions: {
              page: currentPage,
              limit: 50
            },
            orderBy,
            status: caseFilter === 'close' ? CaseStatus.Completed : CaseStatus.Active,
            searchValue: debouncedSearch,
            lawTypeId: lawTypeId,
            clientNumber: clientNo
          }
        }
      })
    }
    return () => {}
    // eslint-disable-next-line
  }, [
    lawTypeId,
    caseFilter,
    debouncedSearch,
    currentPage,
    sortCasesByUser?.id,
    orderBy,
    clientNo,
    activeTab,
    pageValue
  ])

  useEffect(() => {
    return () => {
      caseDispatch({ type: CaseActionType.SET_ALL_CASES, allCases: [] })
      return reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    caseDispatch({ type: CaseActionType.SET_ALL_CASES, allCases: [] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientNo])

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Button
          startIcon={<ArrowBackIos sx={{ width: '13px' }} />}
          sx={{
            color: '#5B5675',
            fontWeight: 600,
            fontSize: '14px',
            ml: -1
          }}
          onClick={handleBack}
        >
          BACK
        </Button>
      </Box>

      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Typography variant="h6">
          {!client ? 'Loading...' : `(#${client?.clientNumber}) ${client?.displayName}`}
        </Typography>

        <Button variant="outlined" color="primary" onClick={handleOpenEditModal}>
          <Edit color="primary" fontSize="small" />
          <Typography color={'primary'} pl={0.5} fontWeight={'medium'}>
            Edit
          </Typography>
        </Button>
      </Box>

      <TabsContainer
        value={activeTab ?? pageValue}
        onChange={handleChange}
        aria-label="user tab"
        TabIndicatorProps={{ sx: { background: `${palette.darkRed}` } }}
        sx={{ paddingTop: '20px' }}
      >
        <TabItem
          label="General"
          {...a11yProps(0, 'client')}
          iconPosition="start"
          onClick={() => {
            caseDispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
            reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
            const zero = 0
            searchParams.set('activeTab', zero.toString())
            searchParams.set('caseFilter', 'active')
            searchParams.set('currentPage', currentPage.toString())
            setSearchParams(searchParams)
          }}
        />

        <TabItem
          label="Matters"
          {...a11yProps(1, 'client')}
          iconPosition="start"
          onClick={() => {
            caseDispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
            reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
            searchParams.set('activeTab', '1')
            searchParams.set('caseFilter', 'active')
            searchParams.set('currentPage', currentPage.toString())
            setSearchParams(searchParams)
          }}
        />

        <TabItem
          label="Invoices"
          {...a11yProps(2, 'client')}
          iconPosition="start"
          onClick={() => {
            caseDispatch({ type: CaseActionType.SET_CASE_FILTER, caseFilter: 'active' })
            reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
            searchParams.set('activeTab', '2')
            searchParams.set('caseFilter', 'active')
            searchParams.set('currentPage', currentPage.toString())
            setSearchParams(searchParams)
          }}
        />
      </TabsContainer>

      <TabPanel value={activeTab ?? pageValue} index={0}>
        {loading ? <CommonLoader /> : <ClientGeneralCard client={client} />}
      </TabPanel>

      <TabPanel value={activeTab ?? pageValue} index={1}>
        <AllCases
          loading={loading}
          buttonLink={`/matters/create?${QUERY_PARAMS.CLIENT}=${clientNo}`}
          key={clientNo}
        />
      </TabPanel>

      <TabPanel value={activeTab ?? pageValue} index={2}>
        <ClientInvoicesCard client={client} />
      </TabPanel>

      <EditClient userDetails={client} editClientNumberOnly isEditOpen clientRefetch={refetch} />
    </Box>
  )
}

export default ClientDetailContainer
